import { useState, useEffect, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { Wizard, useToast } from '../organisms';
import { formatPhone } from '../lib/formatPhone';
import { STATE_SELECT_OPTIONS, US_STATES } from '../lib/us-states';
import { STAFF_ROLES } from '../lib/roles';
import { userDisplayName } from '../lib/useUserNames';

/**
 * ClientSetupWizard — /v2/clients/new
 *
 * Creates clients through ONE endpoint, `POST /api/workflow/setup-client`
 * (client + primary contact + assignments in a single transaction), in either
 * of two modes:
 *
 *   One client   — the 3-step form: identity → primary contact → team & services.
 *   Several      — a CSV of the SAME fields, previewed row by row, then created
 *                  one POST per row through that same endpoint.
 *
 * The bulk path deliberately does NOT go through the staged-import pipeline:
 * a client is an account relationship, created explicitly, not staged and
 * reviewed like imported parcel data.
 *
 * The CSV columns are `Object.keys(CLIENT_WIZARD_DEFAULTS)` — literally the
 * fields the form collects — so the template can't drift from the form. Add a
 * field to `WizardState` and it appears in the template, in the preview, and in
 * the payload without a second edit.
 *
 * Additional contacts (the v1 wizard supports dynamic rows) are deferred
 * to the Client detail page Contacts tab — keeps the wizard linear +
 * scannable and lets operators add contacts incrementally instead of
 * front-loading every contact at create time.
 */

interface UsersListResponse {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
}

interface OrgResponse {
  id: string;
  current_system_year: number | null;
}

interface SetupClientResponse {
  client: { id: string; name: string };
}

interface WizardState {
  // Identity
  name: string;
  short_name: string;
  category: string;
  state: string;
  city: string;
  // Primary contact
  primary_first_name: string;
  primary_last_name: string;
  primary_email: string;
  primary_phone: string;
  primary_title: string;
  // Team & services. The client team (ATA part 3 §5c): Manager and Originator are required
  // when the firm has billing fields block save; the Assistant is optional. The legacy
  // assigned_agent / assigned_admin columns are not collected: setup-client derives them
  // from the Manager and the Assistant.
  client_manager_user_id: string;
  originator_user_id: string;
  admin_associate_user_id: string;
  year: string;
  contract_type: string;
  re_services: string;
  pp_services: string;
}

/**
 * The fields this wizard collects, with the value used when a field is left
 * alone. Exported because it IS the CSV contract: the template header, the
 * per-column validation, and the blank-cell fallback all derive from it.
 */
export const CLIENT_WIZARD_DEFAULTS: WizardState = {
  name: '', short_name: '', category: 'Commercial', state: 'TX', city: '',
  primary_first_name: '', primary_last_name: '', primary_email: '', primary_phone: '', primary_title: '',
  client_manager_user_id: '', originator_user_id: '', admin_associate_user_id: '',
  year: '', contract_type: 'Annual', re_services: 'Full Service', pp_services: 'Full Service',
};

const EMPTY = CLIENT_WIZARD_DEFAULTS;

type FieldKey = keyof WizardState;

/** CSV header row — one column per collected field, in form order. */
export const CLIENT_CSV_COLUMNS = Object.keys(CLIENT_WIZARD_DEFAULTS) as FieldKey[];

/** The fields `setup-client` refuses without (mirrors the form's own gating). */
const REQUIRED_FIELDS: FieldKey[] = [
  'name', 'short_name', 'primary_first_name', 'primary_last_name', 'primary_email',
];

/** Human labels — used by the form's Field captions AND by CSV problem text. */
export const FIELD_LABELS: Record<FieldKey, string> = {
  name: 'Client Name',
  short_name: 'Short Name',
  category: 'Category',
  state: 'State',
  city: 'City',
  primary_first_name: 'First Name',
  primary_last_name: 'Last Name',
  primary_email: 'Email',
  primary_phone: 'Phone',
  primary_title: 'Title',
  client_manager_user_id: 'Client Manager',
  originator_user_id: 'Originator',
  admin_associate_user_id: 'Assistant',
  year: 'Tax Year',
  contract_type: 'Contract Type',
  re_services: 'RE Services',
  pp_services: 'BPP Services',
};

// Option vocabularies — one declaration feeding both the form's Selects and the
// CSV validator, so a CSV can never be judged against a list the form doesn't offer.
const CATEGORY_OPTIONS = [
  { value: 'Commercial', label: 'Commercial' },
  { value: 'Residential', label: 'Residential' },
  { value: 'BPP Only', label: 'BPP Only' },
];
const CONTRACT_TYPE_OPTIONS = [
  { value: 'Annual', label: 'Annual' },
  { value: 'Multi-Year', label: 'Multi-Year' },
  { value: 'Project', label: 'Project' },
];
const RE_SERVICE_OPTIONS = [
  { value: 'Full Service', label: 'Full Service' },
  { value: 'Appeal Only', label: 'Appeal Only' },
  { value: 'Tracking Only', label: 'Tracking Only' },
  { value: 'None', label: 'None' },
];
const PP_SERVICE_OPTIONS = [
  { value: 'Full Service', label: 'Full Service' },
  { value: 'Rendition Only', label: 'Rendition Only' },
  { value: 'Tracking Only', label: 'Tracking Only' },
  { value: 'None', label: 'None' },
];
const ENUM_FIELDS: Partial<Record<FieldKey, { value: string }[]>> = {
  category: CATEGORY_OPTIONS,
  contract_type: CONTRACT_TYPE_OPTIONS,
  re_services: RE_SERVICE_OPTIONS,
  pp_services: PP_SERVICE_OPTIONS,
};

/** A filled-in example row, so the template shows the shape rather than describing it. */
const CSV_SAMPLE_ROW: WizardState = {
  name: 'Greystar Real Estate - MK',
  short_name: 'Greystar-MK',
  category: 'Commercial',
  state: 'TX',
  city: 'San Antonio',
  primary_first_name: 'Jane',
  primary_last_name: 'Ruiz',
  primary_email: 'jane@client.com',
  primary_phone: '210-555-0134',
  primary_title: 'VP of Real Estate',
  client_manager_user_id: '',
  originator_user_id: '',
  admin_associate_user_id: '',
  year: '',
  contract_type: 'Annual',
  re_services: 'Full Service',
  pp_services: 'Full Service',
};

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** The downloadable template: the collected fields, plus one example row. */
export function buildClientCsvTemplate(): string {
  const header = CLIENT_CSV_COLUMNS.map(csvCell).join(',');
  const sample = CLIENT_CSV_COLUMNS.map(c => csvCell(CSV_SAMPLE_ROW[c])).join(',');
  return `${header}\n${sample}\n`;
}

/** RFC 4180-ish reader: quoted fields, escaped quotes, CRLF or LF line ends. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else if (ch !== '\r') {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); rows.push(row); }
  // Drop blank lines — a trailing newline shouldn't read as an empty client.
  return rows.filter(r => r.some(c => c.trim() !== ''));
}

/**
 * Read a picked file as text. `Blob.text()` isn't universal (older Safari, and
 * the jsdom build the component tests run in), so fall back to FileReader —
 * which every browser that can render this app has.
 */
function readFileText(file: File): Promise<string> {
  if (typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read that file.'));
    reader.readAsText(file);
  });
}

const normalizeHeader = (s: string) => s.trim().toLowerCase().replace(/[\s-]+/g, '_');

/** Header cell → field key. Accepts the machine column or its human label. */
const HEADER_ALIASES: Record<string, FieldKey> = (() => {
  const map: Record<string, FieldKey> = {};
  for (const key of CLIENT_CSV_COLUMNS) {
    map[normalizeHeader(key)] = key;
    map[normalizeHeader(FIELD_LABELS[key])] = key;
  }
  return map;
})();

export interface CsvPreviewRow {
  /** 1-based position among the DATA rows — the number the user sees in their sheet. */
  rowNumber: number;
  values: WizardState;
  problems: string[];
}

export interface CsvParseResult {
  rows: CsvPreviewRow[];
  fileProblems: string[];
  ignoredColumns: string[];
}

function resolveUserReference(raw: string, users: UsersListResponse[]): { id: string; problem?: string } {
  const needle = raw.trim().toLowerCase();
  const match = users.find(u => u.id.toLowerCase() === needle || (u.email || '').toLowerCase() === needle);
  if (match) return { id: match.id };
  // Only claim "unknown" once the team list has actually loaded; otherwise every
  // row would be flagged by a check that has nothing to compare against.
  if (users.length > 0) return { id: '', problem: 'no user with that id or email' };
  return { id: raw.trim() };
}

/**
 * Turn CSV text into preview rows. Every problem is attached to the row it came
 * from — a bad row is shown and skipped, never silently dropped and never
 * fatal to the rows around it.
 */
export function buildCsvRows(
  text: string,
  defaults: WizardState,
  users: UsersListResponse[],
): CsvParseResult {
  const grid = parseCsv(text);
  if (grid.length === 0) {
    return { rows: [], fileProblems: ['This file has no rows.'], ignoredColumns: [] };
  }

  const headerCells = grid[0];
  const ignoredColumns: string[] = [];
  const columnAt: (FieldKey | null)[] = headerCells.map(cell => {
    const key = HEADER_ALIASES[normalizeHeader(cell)];
    if (!key && cell.trim() !== '') ignoredColumns.push(cell.trim());
    return key ?? null;
  });

  const fileProblems: string[] = [];
  const present = new Set(columnAt.filter(Boolean) as FieldKey[]);
  const missingRequired = REQUIRED_FIELDS.filter(f => !present.has(f));
  if (missingRequired.length > 0) {
    fileProblems.push(
      `The file has no ${missingRequired.map(f => FIELD_LABELS[f]).join(', ')} column — download the template and use its header row.`,
    );
  }

  const rows: CsvPreviewRow[] = grid.slice(1).map((cells, i) => {
    const values: WizardState = { ...defaults };
    const problems: string[] = [];

    columnAt.forEach((key, col) => {
      if (!key) return;
      const raw = (cells[col] ?? '').trim();
      if (raw === '') return; // blank cell → the form's own default
      values[key] = raw;
    });

    for (const field of REQUIRED_FIELDS) {
      if (values[field].trim() === '') problems.push(`${FIELD_LABELS[field]} is required.`);
    }

    if (values.primary_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.primary_email)) {
      problems.push(`“${values.primary_email}” is not a valid email address.`);
    }

    if (values.state) {
      const code = values.state.toUpperCase();
      if (!(US_STATES as readonly string[]).includes(code)) {
        problems.push(`“${values.state}” is not a US state code.`);
      } else {
        values.state = code;
      }
    }

    for (const [field, options] of Object.entries(ENUM_FIELDS) as [FieldKey, { value: string }[]][]) {
      const raw = values[field];
      if (!raw) continue;
      const match = options.find(o => o.value.toLowerCase() === raw.toLowerCase());
      if (match) values[field] = match.value;
      else problems.push(`${FIELD_LABELS[field]} “${raw}” is not one of: ${options.map(o => o.value).join(', ')}.`);
    }

    if (values.year && !/^\d{4}$/.test(values.year)) {
      problems.push(`Tax Year “${values.year}” is not a four-digit year.`);
    }

    if (values.primary_phone) values.primary_phone = formatPhone(values.primary_phone);

    for (const field of ['client_manager_user_id', 'originator_user_id', 'admin_associate_user_id'] as FieldKey[]) {
      const raw = values[field];
      if (!raw) continue;
      const resolved = resolveUserReference(raw, users);
      values[field] = resolved.id;
      if (resolved.problem) problems.push(`${FIELD_LABELS[field]} “${raw}” — ${resolved.problem}.`);
    }

    return { rowNumber: i + 1, values, problems };
  });

  return { rows, fileProblems, ignoredColumns };
}

/** The one request body both modes send — the single-client contract, per row. */
function buildSetupClientPayload(values: WizardState) {
  // Endpoint expects { client: {...}, contacts: [...] } per src/routes/workflows.js
  // setup-client handler. v1 wizard at public/index.html submitClientWizard sends
  // the same shape.
  return {
    client: {
      name: values.name,
      short_name: values.short_name,
      status: 'active' as const,
      category: values.category || null,
      state: values.state || null,
      city: values.city || null,
      country: 'US',
      client_manager_user_id: values.client_manager_user_id || null,
      originator_user_id: values.originator_user_id || null,
      admin_associate_user_id: values.admin_associate_user_id || null,
      year: values.year ? Number(values.year) : null,
      contract_type: values.contract_type || null,
      re_services: values.re_services || null,
      pp_services: values.pp_services || null,
    },
    contacts: [{
      role: 'primary',
      first_name: values.primary_first_name,
      last_name: values.primary_last_name,
      email: values.primary_email,
      phone: values.primary_phone || null,
      title: values.primary_title || null,
    }],
  };
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    return String((err.body as { error: unknown }).error);
  }
  return err instanceof Error ? err.message : fallback;
}

type SetupMode = 'single' | 'bulk';

export function ClientSetupWizard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [mode, setMode] = useState<SetupMode>('single');
  const [state, setState] = useState<WizardState>(EMPTY);

  // CSV mode
  const [csvFileName, setCsvFileName] = useState('');
  const [csvResult, setCsvResult] = useState<CsvParseResult | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  // Client ids already created this session, keyed by CSV row number — so a retry
  // after a mid-batch failure SKIPS the rows that already succeeded instead of
  // creating a second client for each of them.
  const createdRef = useRef<Record<number, string>>({});

  const usersQuery = useQuery<UsersListResponse[]>({
    queryKey: ['users-list'],
    queryFn: ({ signal }) => api.get<UsersListResponse[]>('/api/users-list', signal),
  });

  const orgQuery = useQuery<OrgResponse[]>({
    queryKey: ['organizations'],
    queryFn: ({ signal }) => api.get<OrgResponse[]>('/api/organizations', signal),
  });

  // ATA part 3 §5c: when the firm has billing fields block save, Manager and Originator are required here.
  const requiredQuery = useQuery<{ billing_required_fields?: { enabled?: boolean } }>({
    queryKey: ['org-settings', 'billing-required-fields'],
    queryFn: ({ signal }) => api.get('/api/org-settings/billing-required-fields', signal),
    retry: false,
  });
  const billingRequired = requiredQuery.data?.billing_required_fields?.enabled === true;

  // Default year once org lands
  useEffect(() => {
    if (orgQuery.data && orgQuery.data[0] && !state.year) {
      const yr = orgQuery.data[0].current_system_year || new Date().getFullYear();
      setState(s => ({ ...s, year: String(yr) }));
    }
  }, [orgQuery.data, state.year]);

  const allUsers = usersQuery.data ?? [];
  // Every Team picker offers the firm's staff, by name. No picker filters on a single role:
  // that is how 'admin' (retired, now 'administrator') and 'agent' left ATA with empty lists.
  const staffOptions = allUsers
    .filter(u => STAFF_ROLES.has(u.role))
    .map(u => ({ value: u.id, label: userDisplayName(u) }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const baseYear = orgQuery.data?.[0]?.current_system_year || new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => baseYear + i);

  const set = <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => setState(s => ({ ...s, [k]: v }));

  const mutation = useMutation<SetupClientResponse, Error, void>({
    mutationFn: async () => api.post<SetupClientResponse>('/api/workflow/setup-client', buildSetupClientPayload(state)),
    onSuccess: (data) => {
      toast({ variant: 'success', message: `Client "${state.name}" created.` });
      // Refresh the Clients list so the new client is present when the operator
      // navigates back within the 60s staleTime window (mirrors ParcelSetupWizard).
      qc.invalidateQueries({ queryKey: ['clients-list'] });
      navigate(`/clients/${data.client.id}`);
    },
    onError: (err) => {
      toast({ variant: 'error', message: errorMessage(err, 'Could not create the client.') });
    },
  });

  const canAdvance = (step: number): boolean => {
    if (step === 0) return state.name.trim().length > 0 && state.short_name.trim().length > 0;
    if (step === 1) {
      return state.primary_first_name.trim().length > 0
        && state.primary_last_name.trim().length > 0
        && state.primary_email.trim().length > 0;
    }
    if (step === 2 && billingRequired) {
      return state.client_manager_user_id !== '' && state.originator_user_id !== '';
    }
    return true;
  };

  // ── CSV mode ───────────────────────────────────────────────────────────────
  const csvDefaults: WizardState = {
    ...EMPTY,
    year: String(baseYear),
  };
  const csvRows = csvResult?.rows ?? [];
  const creatableRows = csvRows.filter(r => r.problems.length === 0);
  const skippedCount = csvRows.length - creatableRows.length;

  function downloadTemplate() {
    const blob = new Blob([buildClientCsvTemplate()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'appealstack-new-clients-template.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function onPickCsv(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // A new file means new row numbers — anything remembered from a previous
    // batch would skip the wrong rows.
    createdRef.current = {};
    setCsvFileName(file.name);
    setCsvResult(null);

    let text: string;
    try {
      text = await readFileText(file);
    } catch {
      toast({ variant: 'error', message: 'Could not read that file. Try choosing it again.' });
      return;
    }
    if (text.includes('\u0000') || text.startsWith('PK')) {
      toast({ variant: 'error', message: 'This looks like a binary file. Export the sheet as CSV first.' });
      return;
    }
    setCsvResult(buildCsvRows(text, csvDefaults, allUsers));
  }

  async function submitBulk() {
    setBulkSubmitting(true);
    try {
      const createdIds: string[] = [];
      for (const row of creatableRows) {
        // Reuse an id created on a prior (failed) attempt so a retry never
        // creates a second client for a row that already succeeded.
        let id = createdRef.current[row.rowNumber];
        if (!id) {
          const res = await api.post<SetupClientResponse>('/api/workflow/setup-client', buildSetupClientPayload(row.values));
          id = res.client.id;
          createdRef.current[row.rowNumber] = id;
        }
        createdIds.push(id);
      }
      qc.invalidateQueries({ queryKey: ['clients-list'] });
      createdRef.current = {};
      toast({ variant: 'success', message: `${createdIds.length} client${createdIds.length === 1 ? '' : 's'} created.` });
      navigate('/clients');
    } catch (err) {
      const created = Object.keys(createdRef.current).length;
      const base = errorMessage(err, 'Could not finish creating the clients.');
      toast({
        variant: 'error',
        message: created > 0
          ? `${base} — ${created} client${created === 1 ? ' was' : 's were'} already created; retry to finish the rest.`
          : base,
      });
    } finally {
      setBulkSubmitting(false);
    }
  }

  const canAdvanceBulk = (step: number): boolean => {
    if (step === 0) return csvRows.length > 0;
    return creatableRows.length > 0;
  };

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>New Client Setup</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          Onboard one client step by step, or create a batch of them from a CSV of the same fields.
        </p>
      </header>

      <div style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        padding: 'var(--sp-6)',
      }}>
        <ModePicker mode={mode} onChange={setMode} />

        {mode === 'single' ? (
          <Wizard
            key="single"
            steps={[
              { label: 'Identity', description: 'Client basics' },
              { label: 'Primary contact', description: 'Required' },
              { label: 'Team & services', description: 'Review + create' },
            ]}
            onSubmit={() => mutation.mutate()}
            onCancel={() => navigate('/clients')}
            submitting={mutation.isPending}
            submitLabel="Create client"
            canAdvance={canAdvance}
          >
            {({ currentStep }) => (
              <>
                {currentStep === 0 && (
                  <StepIdentity state={state} set={set} />
                )}
                {currentStep === 1 && (
                  <StepPrimaryContact state={state} set={set} />
                )}
                {currentStep === 2 && (
                  <StepTeamAndServices
                    state={state}
                    set={set}
                    staffOptions={staffOptions}
                    yearOptions={yearOptions}
                    billingRequired={billingRequired}
                  />
                )}
              </>
            )}
          </Wizard>
        ) : (
          <Wizard
            key="bulk"
            steps={[
              { label: 'CSV file', description: 'Template + upload' },
              { label: 'Review & create', description: 'Row by row' },
            ]}
            onSubmit={submitBulk}
            onCancel={() => navigate('/clients')}
            submitting={bulkSubmitting}
            submitLabel={`Create ${creatableRows.length} client${creatableRows.length === 1 ? '' : 's'}`}
            canAdvance={canAdvanceBulk}
          >
            {({ currentStep }) => (
              <>
                {currentStep === 0 && (
                  <StepCsvUpload
                    fileName={csvFileName}
                    rowCount={csvRows.length}
                    onDownloadTemplate={downloadTemplate}
                    onPickFile={onPickCsv}
                  />
                )}
                {currentStep === 1 && (
                  <StepCsvReview result={csvResult} creatable={creatableRows.length} skipped={skippedCount} />
                )}
              </>
            )}
          </Wizard>
        )}
      </div>
    </>
  );
}

function ModePicker({ mode, onChange }: { mode: SetupMode; onChange: (m: SetupMode) => void }) {
  const options: { value: SetupMode; label: string; hint: string }[] = [
    { value: 'single', label: 'One client', hint: 'Fill the form step by step.' },
    { value: 'bulk', label: 'Several clients', hint: 'Upload a CSV of the same fields.' },
  ];
  return (
    <div role="radiogroup" aria-label="How many clients" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
      gap: 'var(--sp-3)',
      marginBottom: 'var(--sp-6)',
    }}>
      {options.map(o => {
        const selected = mode === o.value;
        return (
          <label key={o.value} style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--sp-2)',
            padding: 'var(--sp-3) var(--sp-4)',
            border: `1px solid ${selected ? 'var(--brand-primary)' : 'var(--color-border)'}`,
            background: selected ? 'var(--brand-primary-bg)' : 'var(--color-bg)',
            borderRadius: 'var(--r-md)',
            cursor: 'pointer',
          }}>
            <input
              type="radio"
              name="client-setup-mode"
              value={o.value}
              checked={selected}
              onChange={() => onChange(o.value)}
              style={{ marginTop: 3 }}
            />
            <span>
              <span style={{ display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{o.label}</span>
              <span style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>{o.hint}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function StepCsvUpload({ fileName, rowCount, onDownloadTemplate, onPickFile }: {
  fileName: string;
  rowCount: number;
  onDownloadTemplate: () => void;
  onPickFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div>
        <p style={{ margin: '0 0 var(--sp-2)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
          The template's columns are exactly the fields this form collects. Leave a cell blank to
          take the form's own default for it.
        </p>
        <Button variant="secondary" size="sm" onClick={onDownloadTemplate}>Download CSV template</Button>
      </div>

      <Field label="CSV file" fullWidth>
        <input type="file" accept=".csv,.txt" onChange={onPickFile} style={{ fontFamily: 'inherit' }} />
      </Field>

      {fileName && (
        <p style={{ margin: 0 }}>
          <Badge variant="success" dot>{fileName}</Badge>
          <span style={{ color: 'var(--color-muted)', marginLeft: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
            {rowCount} row{rowCount === 1 ? '' : 's'} read
          </span>
        </p>
      )}

      <DocumentsNotAccepted />
    </div>
  );
}

/**
 * The place a user reaches for a document upload — so this is where the reason
 * there isn't one belongs. An option that silently vanishes teaches nobody, and
 * a control that renders disabled just looks broken.
 */
function DocumentsNotAccepted() {
  return (
    <div style={{
      padding: 'var(--sp-3) var(--sp-4)',
      background: 'var(--color-bg)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-md)',
    }}>
      <p style={{ margin: '0 0 var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>
        Why you can't upload documents here
      </p>
      <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)' }}>
        Clients are never created from documents, because no property-tax document names your
        client — an assessor's notice names the owner of record, which is routinely a
        title-holding LLC or trust rather than the firm you contracted with, so reading one
        would file the wrong company as your customer.
      </p>
    </div>
  );
}

function StepCsvReview({ result, creatable, skipped }: {
  result: CsvParseResult | null;
  creatable: number;
  skipped: number;
}) {
  if (!result || result.rows.length === 0) {
    return <p style={{ margin: 0, color: 'var(--color-muted)', fontSize: 'var(--fs-sm)' }}>No rows to review yet — choose a CSV file first.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div style={{
        padding: 'var(--sp-3) var(--sp-4)',
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-md)',
        fontSize: 'var(--fs-sm)',
        color: 'var(--color-ink-2)',
      }}>
        Creating <strong>{creatable}</strong> client{creatable === 1 ? '' : 's'} of{' '}
        <strong>{result.rows.length}</strong> row{result.rows.length === 1 ? '' : 's'}
        {skipped > 0 ? <> · <strong>{skipped}</strong> skipped until the problems below are fixed in the file</> : null}.
      </div>

      {result.fileProblems.map((p, i) => (
        <p key={i} style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-status-amber)' }}>{p}</p>
      ))}
      {result.ignoredColumns.length > 0 && (
        <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
          Ignored column{result.ignoredColumns.length === 1 ? '' : 's'}: {result.ignoredColumns.join(', ')}.
        </p>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              {['Row', 'Client Name', 'Short Name', 'Primary contact', 'Email', 'State', 'Status'].map(h => (
                <th key={h} style={{
                  textAlign: 'left', padding: 'var(--sp-2) var(--sp-3)',
                  fontWeight: 'var(--fw-medium)', color: 'var(--color-ink-3)',
                  borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map(row => {
              const bad = row.problems.length > 0;
              return (
                <tr key={row.rowNumber} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={cell}>{row.rowNumber}</td>
                  <td style={cell}>{row.values.name || '—'}</td>
                  <td style={cell}>{row.values.short_name || '—'}</td>
                  <td style={cell}>{[row.values.primary_first_name, row.values.primary_last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td style={cell}>{row.values.primary_email || '—'}</td>
                  <td style={cell}>{row.values.state || '—'}</td>
                  <td style={cell}>
                    {bad ? (
                      <span>
                        <Badge variant="warning" dot>skipped</Badge>
                        <span style={{ display: 'block', marginTop: 'var(--sp-1)', color: 'var(--color-ink-2)' }}>
                          {row.problems.join(' ')}
                        </span>
                      </span>
                    ) : (
                      <Badge variant="success" dot>will be created</Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const cell: React.CSSProperties = { padding: 'var(--sp-2) var(--sp-3)', verticalAlign: 'top', color: 'var(--color-ink)' };

function StepIdentity({ state, set }: { state: WizardState; set: <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => void }) {
  return (
    <Grid>
      <Field label={`${FIELD_LABELS.name} *`} required>
        <Input value={state.name} onChange={e => set('name')(e.target.value)} placeholder="e.g. Greystar Real Estate - MK" required />
      </Field>
      <Field label={`${FIELD_LABELS.short_name} *`} required>
        <Input value={state.short_name} onChange={e => set('short_name')(e.target.value)} placeholder="e.g. Greystar-MK" required />
      </Field>
      <Field label={FIELD_LABELS.category}>
        <Select
          value={state.category}
          onChange={e => set('category')(e.target.value)}
          options={CATEGORY_OPTIONS}
        />
      </Field>
      <Field label={FIELD_LABELS.state}>
        {/* RH§3 — state is a dropdown of the canonical US codes, not free text. */}
        <Select value={state.state} onChange={e => set('state')(e.target.value)} options={STATE_SELECT_OPTIONS} />
      </Field>
      <Field label={FIELD_LABELS.city}>
        <Input value={state.city} onChange={e => set('city')(e.target.value)} placeholder="San Antonio" />
      </Field>
    </Grid>
  );
}

function StepPrimaryContact({ state, set }: { state: WizardState; set: <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => void }) {
  return (
    <Grid>
      <Field label={`${FIELD_LABELS.primary_first_name} *`} required>
        <Input value={state.primary_first_name} onChange={e => set('primary_first_name')(e.target.value)} required />
      </Field>
      <Field label={`${FIELD_LABELS.primary_last_name} *`} required>
        <Input value={state.primary_last_name} onChange={e => set('primary_last_name')(e.target.value)} required />
      </Field>
      <Field label={`${FIELD_LABELS.primary_email} *`} required>
        <Input type="email" value={state.primary_email} onChange={e => set('primary_email')(e.target.value)} required placeholder="jane@client.com" />
      </Field>
      <Field label={FIELD_LABELS.primary_phone}>
        <Input value={state.primary_phone} onChange={e => set('primary_phone')(formatPhone(e.target.value))} inputMode="tel" placeholder="XXX-XXX-XXXX" />
      </Field>
      <Field label={FIELD_LABELS.primary_title} fullWidth>
        <Input value={state.primary_title} onChange={e => set('primary_title')(e.target.value)} placeholder="VP of Real Estate" />
      </Field>
    </Grid>
  );
}

function StepTeamAndServices({
  state, set, staffOptions, yearOptions, billingRequired,
}: {
  state: WizardState;
  set: <K extends keyof WizardState>(k: K) => (v: WizardState[K]) => void;
  staffOptions: { value: string; label: string }[];
  yearOptions: number[];
  billingRequired: boolean;
}) {
  const missing = (v: string) => billingRequired && v === '';
  return (
    <Grid>
      {/* ATA part 3 §5c: the client team. Manager and Originator block save when the firm asks for it. */}
      <Field label={FIELD_LABELS.client_manager_user_id} required={billingRequired} error={missing(state.client_manager_user_id) ? 'Required before this client can be invoiced' : undefined}>
        <Select value={state.client_manager_user_id} onChange={e => set('client_manager_user_id')(e.target.value)} placeholder="— None —" options={staffOptions} invalid={missing(state.client_manager_user_id)} />
      </Field>
      <Field label={FIELD_LABELS.originator_user_id} required={billingRequired} error={missing(state.originator_user_id) ? 'Required before this client can be invoiced' : undefined}>
        <Select value={state.originator_user_id} onChange={e => set('originator_user_id')(e.target.value)} placeholder="— None —" options={staffOptions} invalid={missing(state.originator_user_id)} />
      </Field>
      <Field label={FIELD_LABELS.admin_associate_user_id}>
        <Select value={state.admin_associate_user_id} onChange={e => set('admin_associate_user_id')(e.target.value)} placeholder="— None —" options={staffOptions} />
      </Field>
      <Field label={FIELD_LABELS.year}>
        <Select
          value={state.year}
          onChange={e => set('year')(e.target.value)}
          options={yearOptions.map(y => ({ value: String(y), label: String(y) }))}
        />
      </Field>
      <Field label={FIELD_LABELS.contract_type}>
        <Select
          value={state.contract_type}
          onChange={e => set('contract_type')(e.target.value)}
          placeholder="—"
          options={CONTRACT_TYPE_OPTIONS}
        />
      </Field>
      <Field label={FIELD_LABELS.re_services}>
        <Select
          value={state.re_services}
          onChange={e => set('re_services')(e.target.value)}
          options={RE_SERVICE_OPTIONS}
        />
      </Field>
      <Field label={FIELD_LABELS.pp_services}>
        <Select
          value={state.pp_services}
          onChange={e => set('pp_services')(e.target.value)}
          options={PP_SERVICE_OPTIONS}
        />
      </Field>
    </Grid>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))',
      gap: 'var(--sp-4)',
    }}>{children}</div>
  );
}

function Field({ label, required, fullWidth, error, children }: { label: string; required?: boolean; fullWidth?: boolean; error?: string; children: ReactNode }) {
  return (
    <label style={{
      display: 'block',
      gridColumn: fullWidth ? '1 / -1' : undefined,
    }}>
      <span style={{
        display: 'block',
        marginBottom: 'var(--sp-1)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--color-ink)',
      }}>
        {label}
        {required && (
          <span aria-hidden="true" style={{
            color: 'var(--color-status-red)',
            marginLeft: 2,
          }}>*</span>
        )}
      </span>
      {children}
      {error && (
        <span role="alert" style={{ display: 'block', marginTop: 'var(--sp-1)', fontSize: 'var(--fs-sm)', color: 'var(--color-status-red)' }}>{error}</span>
      )}
    </label>
  );
}
